import React from "react";
import StarRating from "./StarRating";
import { HISTOGRAM_KEYS, RATING_MAX, SUB_RATING_KEYS } from "../contract";
import { EMPTY, fmtAvg } from "../format";
import { REVIEWS } from "../../constants/testIds/reviews";

/**
 * The Fiverr-style breakdown of a practice's rating: the big average, its star row, the
 * review count, a 5→1 histogram and the three sub-rating averages.
 *
 * PRESENTATIONAL: `t` is the caller's `marketplace`-bound translator, passed in rather
 * than bound here, so the same component renders on the platform's dark profile page and
 * inside a suite that mocks nothing. The keys are explicit literals — `SUB_LABEL_KEYS` maps
 * each member of `SUB_RATING_KEYS` to its own key — because a key BUILT from a value is
 * invisible to `i18n/__tests__/keyCoverage.test.js` and would ship a raw identifier the
 * day a fourth facet is added without its three translations.
 *
 * EVERY BAR IS A FRACTION OF THE TOTAL, `count / review_count`, and a total of zero is a
 * width of 0 % — never `NaN%`, which a browser drops silently so the bar would render at
 * its full track width and claim five stars nobody gave. Each histogram row is ONE
 * sentence to a screen reader ("5 stars: 2 of 4 reviews") and the bar itself is
 * `aria-hidden`; the visible number and the bar are the same fact twice.
 *
 * `grid-cols-1 sm:grid-cols-[auto,1fr]` and `flex-1 min-w-0` on the bars: the widest
 * label column ("Rapport qualité-prix") must not force the bars off a 320 px screen, and
 * a flex child with content may not shrink below that content without `min-w-0`.
 *
 * @param {object} summary a `rating_summary` (contract §3)
 * @param {function} t `useT("marketplace")`
 * @param {"dark"|"light"} [variant]
 */
const SUB_LABEL_KEYS = {
  communication: "rating.sub.communication",
  expertise: "rating.sub.expertise",
  value: "rating.sub.value",
};

const TONES = {
  dark: {
    panel: "bg-white/[0.03] border-white/10", big: "text-white", muted: "text-white/60",
    track: "bg-white/10", bar: "bg-sol-gold", label: "text-white/80",
  },
  light: {
    panel: "bg-white border-eh-border-soft", big: "text-eh-ink", muted: "text-eh-muted",
    track: "bg-eh-cream-deep", bar: "bg-amber-700", label: "text-eh-ink",
  },
};

/**
 * ONE filled glyph beside each histogram row's number — a bullet, not a rating.
 *
 * Drawn with the SAME component rather than a second star path: a full-value display whose
 * box is clipped to the width of a single `sm` glyph (`w-3.5`), so the row's fill overlay
 * covers that glyph entirely. Rendering `value={1}` instead would size the overlay at 20 %
 * of the clipped box — a sliver of a star, which is what a fraction MEANS here and is not
 * what this mark is for. `label=""` keeps it out of the accessibility tree: the row already
 * carries the whole sentence.
 */
const ONE_STAR = "shrink-0 w-3.5 overflow-hidden";

export default function RatingSummary({ summary, t, variant = "dark" }) {
  const tone = TONES[variant] || TONES.dark;
  const total = Number(summary?.review_count) || 0;
  const histogram = summary?.histogram || {};
  const subAvg = summary?.sub_avg || {};
  const avg = fmtAvg(summary?.avg_rating);
  const countText = total === 1 ? t("rating.reviewCount.one") : t("rating.reviewCount.many", { count: total });

  return (
    <section
      data-testid={REVIEWS.ratingSummary}
      className={`rounded-2xl border p-6 ${tone.panel}`}
      aria-label={t("rating.summaryTitle")}
    >
      <h2 className={`font-display text-xl mb-4 ${tone.big}`}>{t("rating.summaryTitle")}</h2>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-6">
        <div className={`font-display text-5xl tabular-nums leading-none ${tone.big}`} data-testid="rating-summary-avg">
          {avg}
        </div>
        <div className="flex flex-col gap-1">
          <StarRating
            value={summary?.avg_rating ?? 0}
            readOnly
            variant={variant}
            size="md"
            label={total ? t("rating.ariaStars", { avg }) : t("rating.noReviews")}
          />
          <span className={`text-sm ${tone.muted}`} data-testid="rating-summary-count">
            {total ? countText : t("rating.noReviews")}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-x-8 gap-y-6">
        <ul className="m-0 p-0 list-none space-y-2 min-w-0" data-testid="rating-histogram">
          {HISTOGRAM_KEYS.map((stars) => {
            const count = Number(histogram[stars]) || 0;
            const pct = total > 0 ? Math.round((count / total) * 1000) / 10 : 0;
            return (
              <li
                key={stars}
                data-testid={REVIEWS.ratingHistogramRow(stars)}
                aria-label={t("rating.histogramRow", { stars, count, total })}
                className="flex items-center gap-3 text-sm"
              >
                <span className={`w-6 shrink-0 tabular-nums text-right ${tone.label}`} aria-hidden="true">{stars}</span>
                <StarRating value={RATING_MAX} readOnly variant={variant} size="sm" label="" className={ONE_STAR} />
                <span className={`flex-1 min-w-0 h-2 rounded-full overflow-hidden ${tone.track}`} aria-hidden="true">
                  <span
                    className={`block h-full rounded-full ${tone.bar}`}
                    style={{ width: `${pct}%` }}
                    data-testid={`${REVIEWS.ratingHistogramRow(stars)}-bar`}
                  />
                </span>
                <span className={`w-8 shrink-0 tabular-nums text-right ${tone.muted}`} aria-hidden="true">{count}</span>
              </li>
            );
          })}
        </ul>

        <dl className="m-0 space-y-2 min-w-0" data-testid="rating-sub-averages">
          {SUB_RATING_KEYS.map((key) => {
            const v = subAvg[key];
            return (
              <div
                key={key}
                data-testid={REVIEWS.ratingSubAvg(key)}
                className="flex items-center justify-between gap-4 text-sm"
              >
                <dt className={`min-w-0 truncate ${tone.label}`}>{t(SUB_LABEL_KEYS[key])}</dt>
                <dd className="m-0 flex items-center gap-2 shrink-0">
                  <StarRating
                    value={v ?? 0}
                    readOnly
                    variant={variant}
                    size="sm"
                    label={v == null ? EMPTY : t("rating.ariaStars", { avg: fmtAvg(v) })}
                  />
                  <span className={`tabular-nums w-8 text-right ${tone.big}`}>{fmtAvg(v)}</span>
                </dd>
              </div>
            );
          })}
        </dl>
      </div>
    </section>
  );
}
